import { capitalize } from "lodash-es";

import type { Node } from "../graph/types.ts";
import type {
  AssessmentScopeIntegration,
  Provider,
  ProviderOrAll,
} from "../types/assessment.ts";
import { ALL_SCOPE_SENTINEL, toScope } from "../types/assessment.ts";
import type { CredentialNode } from "../types/assessment/data/credential.ts";
import type {
  AnyNode,
  AssessmentNodes,
  GrantNode,
  IdentityNode,
  IdentityType,
  ResourceNode,
} from "../types/assessment/data/index.ts";
import type { Monitor } from "../types/assessment/monitor.ts";
import type { Risk } from "../types/catalog.ts";
import { assertNeverError } from "../types/index.ts";
import { ProviderNodeTitles, identityTypeToLabel } from "./constants.ts";

type Label = {
  content: string; // Human-readable content for the given field
  title: string; // Human-readable title for this field
};
export type IdentityNodeLabels = {
  identity: Label;
  identityType: Label;
};

export type GrantNodeLabels = {
  identity: Label;
  identityType: Label;
  privilegeSet: Label;
  resource: Label;
};

export type ResourceNodeLabels = {
  resource: Label;
  service: Label;
  resourceType: Label;
};

export type CredentialNodeLabels = {
  credential: Label;
  identity: Label;
  identityType: Label;
  credentialType: Label;
};

export type NodeLabels =
  | CredentialNodeLabels
  | GrantNodeLabels
  | IdentityNodeLabels
  | ResourceNodeLabels;

export const grantNodeLabels = (
  provider: ProviderOrAll,
  node: GrantNode
): GrantNodeLabels => ({
  identity: {
    title: "Principal",
    content: node.data.principal,
  },
  identityType: {
    content: identityTypeToLabel({
      type: node.data.principalType,
      provider: node.data.provider,
    }),
    title: "Principal Type",
  },
  privilegeSet: {
    content: grantNodeToPrivilegeSet(node),
    title: ProviderNodeTitles[provider].privilegeSet,
  },
  resource: { content: node.data.resources.join(", "), title: "Resource" },
});

export const identityNodeLabels = ({
  data,
}: IdentityNode): IdentityNodeLabels => ({
  identity: { content: data.label, title: "Identity" },
  identityType: { content: identityTypeToLabel(data), title: "Identity Type" },
});

const credentialNodeLabels = ({
  key,
  data,
  aggregates,
}: CredentialNode): CredentialNodeLabels => ({
  identity: { content: aggregates.identity[0]?.label, title: "Identity" },

  identityType: {
    content: identityTypeToLabel({
      type: aggregates.identity[0]?.type,
      provider: data.provider,
    }),
    title: "Identity Type",
  },
  credential: { content: key, title: "Credential ID" },
  credentialType: { content: data.type, title: "Credential Type" },
});

const resourceNodeLabels = ({ data }: ResourceNode): ResourceNodeLabels => ({
  resource: { content: data.locator, title: "Resource" },
  resourceType: {
    content: renderResourceType(data.type),
    title: "Resource Type",
  },
  service: { content: data.service ?? "None", title: "Resource Service" },
});

export const nodeLabels = (
  provider: ProviderOrAll,
  node: AnyNode
): NodeLabels => {
  switch (node.type) {
    case "grant":
      return grantNodeLabels(provider, node as GrantNode);
    case "identity":
      return identityNodeLabels(node as IdentityNode);
    case "credential":
      return credentialNodeLabels(node as CredentialNode);
    case "resource":
      return resourceNodeLabels(node as ResourceNode);
    default:
      throw assertNeverError(node.type);
  }
};

export const providerLabels: Record<Provider, string> = {
  aws: "AWS",
  azure: "Azure",
  "azure-ad": "Entra ID",
  "entra-id": "Entra ID",
  gcp: "Google Cloud",
  custom: "Custom",
  k8s: "Kubernetes",
  okta: "Okta",
  workspace: "Google Workspace",
};

export const grantNodeToPrivilegeSet = (node: Node<AssessmentNodes, "grant">) =>
  // Handles both gcp `projects/1234/roles/foo` and AWS `Policy:Statement`
  // Runs prior to 2025-03-27 use `permissionSet` instead of `privilegeSet`
  node?.data?.privilegeSet?.split("/").at(-1)?.split(":")?.[0] ??
  "Unknown privilege set";

export const findingTitle = (monitor: Monitor, labels: NodeLabels) =>
  `${monitor.label}: ${
    "identity" in labels ? labels.identity.content : labels.resource.content
  }`;

export const riskLink = (risk: Pick<Risk, "id" | "name" | "score">) =>
  `https://catalog.p0.dev/risks/${risk.id}`;

export const privilegeLink = (provider: Provider, privilege: string) =>
  `https://catalog.p0.dev/services/${provider}/${encodeURIComponent(
    privilege
  )}`;

/** Mirrors `@p0security/integrations-aws-backend`'s own `AWS_ROLE_URL`/`awsUrlFor`
 * (`shared/constants.ts`), duplicated locally since `shared` cannot import an
 * integration package. Uses us-west-2 as default region since IAM is global. */
const AWS_ROLE_URL = (context: {
  idcId: string;
  accountId: string;
  roleName: string;
}) =>
  `https://${context.idcId}.awsapps.com/start/#/console?account_id=${
    context.accountId
  }&destination=${encodeURIComponent(
    `https://us-west-2.console.aws.amazon.com/iam/home?region=us-west-2#/roles/details/${context.roleName}?section=permissions`
  )}`;

// TODO: change to switch the instance more principal types are added
const awsUrlFor = (principalType: IdentityType) =>
  principalType === "service-account" ? AWS_ROLE_URL : undefined;

export const principalLink = (
  principal: string,
  principalType: IdentityType,
  scopeKey: string,
  integration?: AssessmentScopeIntegration,
  idcId?: string | undefined
): string | undefined => {
  const scope = toScope(scopeKey);
  return scope != ALL_SCOPE_SENTINEL && integration === "aws" && idcId
    ? awsUrlFor(principalType)?.({
        idcId: idcId,
        accountId: scope.id,
        roleName: principal,
      })
    : undefined;
};

export const renderResourceType = (type: string | undefined) =>
  type ? capitalize(type.replace(/-/g, " ")) : "(Entire service)";
